import * as R from 'remeda';
import { useMutation } from '@tanstack/react-query';
import { endpoints, getIDFromUrl } from '@rossum/api-client';
import {
  EditPagesPayload,
  EditPagesResponse,
} from '@rossum/api-client/annotations';
import { Queue } from '@rossum/api-client/queues';
import { isEmbedded } from '../../constants/config';
import { api } from '../../lib/apiClient';
import { isNotNullOrUndefined } from '../../lib/typeGuards';
import {
  ProcessingDuration,
  timeSpent,
} from '../../timers/timeSpent/timeSpent';
import {
  areSuggestedSplitsActive,
  EditDocumentConfig,
  EditState,
  hasSameSequenceOfPages,
  isEditable,
  Part,
  PartWithAccessibleAnnotation,
  PartWithoutAnnotation,
} from './editState';

type EditOperation = EditPagesPayload['edit'][number];

const toEditOperation = (
  part: PartWithAccessibleAnnotation | PartWithoutAnnotation,
  referencesExistingChild = false
): EditOperation => {
  const parentPages = part.pages.map(p => ({
    rotationDeg: p.rotationDeg,
    page: p.url,
    deleted: p.deleted,
  }));

  if (part.annotationType === 'accessible')
    return {
      // Only include the annotation reference when this part is a child of
      // the annotation being edited. Pointing at the API-call annotation
      // itself triggers a "is not child of itself" rejection on the BE.
      ...(referencesExistingChild ? { annotation: part.annotation.url } : {}),
      targetQueue: part.targetQueueUrl,
      parentPages,
      values: part.editValues,
    };

  return {
    targetQueue: part.targetQueueUrl,
    parentPages,
    values: part.editValues,
  };
};

type MoveOperation = EditPagesPayload['move'][number];

const toMoveOperation = (
  part: PartWithAccessibleAnnotation
): MoveOperation => ({
  annotation: part.annotation.url,
  targetQueue: part.targetQueueUrl,
});

const determineOperationForPart = (
  part: Part,
  config: EditDocumentConfig,
  splitDocument = false
) => {
  if (part.annotationType === 'inaccessible') return null;

  // couldn't be changed
  if (!isEditable(part)) return null;

  if (part.annotationType === 'none') {
    // new part assembled from previously-loose pages — always creates a child
    return toEditOperation(part);
  }

  // accessible
  const { annotation } = part;

  // The annotation can't be changed or added later, so we are sure such a part exists
  const correspondingPartInInitialState = config.initialParts.find(
    (p): p is PartWithAccessibleAnnotation =>
      p.annotationType === 'accessible' && p.annotation === annotation
  )!;

  const pagesAreChanged = !hasSameSequenceOfPages(
    part,
    correspondingPartInInitialState
  );
  const targetQueueHasChanged =
    correspondingPartInInitialState.targetQueueUrl !== part.targetQueueUrl;
  const editValuesHaveChanged = !R.isDeepEqual(
    part.editValues,
    correspondingPartInInitialState.editValues
  );

  // Nothing changed → NoOp.
  if (!pagesAreChanged && !targetQueueHasChanged && !editValuesHaveChanged) {
    return null;
  }

  // An edit op references an existing child only in parent-annotation mode,
  // where the part's annotation differs from the API-call annotation. Pointing
  // an edit at the call annotation itself is rejected by the BE ("is not child
  // of itself"). On the non-split path edits never reference a child.
  const referencesExistingChild = part.annotation.url !== config.annotationUrl;
  const onlyQueueChanged =
    targetQueueHasChanged && !pagesAreChanged && !editValuesHaveChanged;
  // single-annotation is the original, un-split document; the other two modes
  // operate on a child produced by a split (the part itself in child-annotation
  // mode, a selected child in parent-annotation mode).
  const partRepresentsChild = config.mode.type !== 'single-annotation';

  // MoveOp: a queue-only change on a child, or any queue-only change when the
  // queue isn't configured to always split. single-annotation always splits on
  // a split-mode confirm — this confirm IS the first split, so move[] (which
  // would just relocate the batch and skip the split) is wrong there.
  if (onlyQueueChanged && (partRepresentsChild || !splitDocument)) {
    return toMoveOperation(part);
  }

  // EditOp.
  return toEditOperation(part, referencesExistingChild && splitDocument);
};

const collectAnnotationsToDelete = (editState: EditState) => {
  return editState.config.initialParts
    .filter(
      (p): p is PartWithAccessibleAnnotation =>
        p.annotationType === 'accessible' &&
        p.annotation.started &&
        // The original document should never be sent to delete
        p.annotation.url !== editState.config.annotationUrl
    )
    .flatMap(initialPart => {
      const currentPartWithTheSameAnnotation = editState.parts.find(
        pp =>
          pp.annotationType === 'accessible' &&
          pp.annotation.url === initialPart.annotation.url
      );

      if (currentPartWithTheSameAnnotation) {
        if (
          hasSameSequenceOfPages(initialPart, currentPartWithTheSameAnnotation)
        ) {
          // Nothing has changed, we can keep the annotation
          return [];
        }

        // The pages have changed, we have to delete the annotation,
        // since a new one will be created.
        return [initialPart.annotation.url];
      }

      // There is no part with the same annotation, so its pages will be used
      // in new annotations. We have to delete it.
      return [initialPart.annotation.url];
    });
};

export const buildConfirmPayload = (
  editState: EditState,
  processingDuration: ProcessingDuration | undefined,
  splitDocument = false,
  forbidTargetQueueChange = false
): EditPagesPayload => {
  const operations = editState.parts
    .map(part =>
      determineOperationForPart(part, editState.config, splitDocument)
    )
    .filter(isNotNullOrUndefined);

  const annotationsToDelete = collectAnnotationsToDelete(editState);

  const moveOperations = operations.filter(
    (op): op is MoveOperation => !('parentPages' in op)
  );
  const editOperations = operations.filter(
    (op): op is EditOperation => 'parentPages' in op
  );

  // Including an annotation in stop_reviewing while it is also being moved,
  // edited or deleted causes the backend to overwrite the just-applied change
  // with stale state (e.g. it reverts the queue change).
  const annotationsBeingModified = new Set([
    ...annotationsToDelete,
    ...moveOperations.map(op => op.annotation),
    ...editOperations.map(op => op.annotation).filter(isNotNullOrUndefined),
  ]);

  const annotationsToStop = editState.config.initialParts.flatMap(p =>
    p.annotationType === 'accessible' &&
    p.annotation.started &&
    !annotationsBeingModified.has(p.annotation.url) &&
    // There is a separate stopParent flag for stopping the original annotation
    p.annotation.url !== editState.config.annotationUrl
      ? [p.annotation.url]
      : []
  );

  return {
    delete: annotationsToDelete,
    // When target queue selection is disabled on the queue, ensure no move
    // operations are sent even if the state somehow contains a changed
    // targetQueueUrl. The UI hides the dropdown so this is defensive only.
    move: forbidTargetQueueChange ? [] : moveOperations,
    edit: editOperations,
    // The original document is stopped as a parent only when a single-annotation
    // confirm actually splits it — i.e. it produced an edit op. A queue-only
    // self-move produces just a move op and no split, so there is no parent to
    // stop; sending stopParent in that case reverts the move (note_946285).
    stopParent:
      editState.config.mode.type === 'single-annotation'
        ? editOperations.length > 0
        : false,
    stopReviewing: annotationsToStop,
    processingDuration,
  };
};

export const hasChangesToConfirm = (editState: EditState) => {
  const {
    config: { initialParts },
    parts,
  } = editState;
  return !R.isDeepEqual(initialParts, parts);
};

type UseConfirmEditModeParams = {
  currentQueue: Queue | undefined;
  // Receives the mutation result. Callers that don't need it may pass a
  // zero-arg function; the response is what the `/edit_pages` family returns.
  onSuccess: (response: EditPagesResponse) => void;
};

export const useConfirmEditMode = ({
  currentQueue,
  onSuccess,
}: UseConfirmEditModeParams) => {
  const targetQueueSelectionEnabled =
    currentQueue?.settings.uiEditTargetQueueSelectionEnabled ?? true;
  const alwaysSplitDocument =
    currentQueue?.settings.uiEditAlwaysSplitDocument ?? false;
  // When the inbox queue defines edit values, a plain move/PATCH drops the
  // `values` payload, so the edit values never reach the target queue and any
  // data-field bindings break (MAT-1742). Forcing the split routes the confirm
  // through `/edit_pages`, where `edit[]` ops carry `values`.
  const queueHasEditValues =
    (currentQueue?.settings.uiEditValues?.length ?? 0) > 0;
  const splitDocument = alwaysSplitDocument || queueHasEditValues;

  return useMutation({
    mutationFn: (editState: EditState) => {
      const annotationId = getIDFromUrl(editState.config.annotationUrl);

      // Split parent won't have timer started, so undefined is a valid result
      const processingDuration = timeSpent.stopAnnotation(annotationId);

      const deletingAnnotation =
        (editState.config.mode.type === 'single-annotation' ||
          editState.config.mode.type === 'child-annotation') &&
        editState.parts.every(p => p.pages.every(page => page.deleted));

      if (deletingAnnotation) {
        return api
          .request(
            endpoints.annotations.deleteEndpoint(
              getIDFromUrl(editState.config.annotationUrl)
            )
          )
          .then<EditPagesResponse>(() => ({ results: [] }));
      }

      const editDataSource =
        editState.config.initialPartsWithSuggestedSplits === null
          ? 'manual'
          : areSuggestedSplitsActive(editState.parts, editState.config)
            ? 'suggest'
            : 'modified_suggest';

      // In order to continue working with a document with only deleted/rotated pages,
      // we have to use in-place endpoint in embedded mode, so that annotation ID is preserved.
      const firstPart = editState.parts[0];
      const inPlace =
        (isEmbedded() || editState.config.settings.preferInPlaceSplitting) &&
        editState.parts.length === 1;

      if (firstPart && inPlace) {
        // Queue-only changes now always route through edit_pages (the direct
        // annotation PATCH shortcut is gone), so the in-place endpoint must
        // forward the queue change itself whenever one happened — otherwise it
        // would be silently dropped. The schema supports targetQueue here.
        const initialFirstPart = editState.config.initialParts[0];
        const queueChangedFromInitial =
          initialFirstPart !== undefined &&
          initialFirstPart.annotationType !== 'inaccessible' &&
          firstPart.annotationType !== 'inaccessible' &&
          initialFirstPart.targetQueueUrl !== firstPart.targetQueueUrl;

        return api.request(
          endpoints.annotations.editPagesInPlace(annotationId, {
            processingDuration: processingDuration?.processingDuration,
            parentPages: firstPart.pages.map(p => ({
              rotationDeg: p.rotationDeg,
              page: p.url,
              deleted: p.deleted,
            })),
            ...(queueChangedFromInitial
              ? { targetQueue: firstPart.targetQueueUrl }
              : {}),
            editDataSource,
          })
        );
      }

      const confirmPayload = buildConfirmPayload(
        editState,
        processingDuration?.processingDuration,
        splitDocument,
        !targetQueueSelectionEnabled
      );

      return api.request(
        endpoints.annotations.editPages(annotationId, {
          ...confirmPayload,
          editDataSource,
        })
      );
    },

    onSuccess,
  });
};
